import api from "./api"

export type TaskStatus = "PENDING" | "ACCEPTED" | "REJECTED" | "COMPLETED" | "CANCELLED"
/** Şirketin görev türü kodu (şirket detayı → Görev Türleri). */
export type TaskType = string

export interface PersonnelTaskType {
  id: string
  companyId: string
  code: string
  name: string
  groupName: string
  roleLabel: string | null
  description: string | null
  requiresProject: boolean
  requiresAuthorization: boolean
  system: boolean
  active: boolean
  sortOrder: number
  taskCount?: number
  _count?: { authorizations: number }
}
export type TaskTypePayload = Partial<Pick<PersonnelTaskType, "code" | "name" | "groupName" | "roleLabel" | "description" | "requiresProject" | "requiresAuthorization" | "active" | "sortOrder">> & { companyId?: string }

export const fetchTaskTypes = async (companyId?: string) => (await api.get<PersonnelTaskType[]>("/personnel-tasks/types", { params: { companyId } })).data
/**
 * Görevlendirme seçicileri için: şirketin türleri; şirket bağlamı yoksa
 * (yönetici, şirkete bağlı olmayan set) varsayılan türler — kodlar her
 * şirkette aynı tohumlanıyor.
 */
/**
 * Şirketin görev türleri; şirketi olmayan bağlamda (platform seti, şirketsiz
 * dosya) varsayılanlar. Şirket VARKEN hata da varsayılana düşüyordu ve şirkette
 * yapılan değişiklik hiç görünmüyordu (ör. personel rolü listeyi okuyamıyordu);
 * yalnız şirket türleri okunamazsa yedek olarak varsayılan kullanılır.
 */
export const fetchTaskTypesOrDefaults = async (companyId?: string | null) => {
  if (!companyId) return (await api.get<PersonnelTaskType[]>("/personnel-tasks/types/defaults")).data
  try {
    return await fetchTaskTypes(companyId ?? undefined)
  } catch {
    return (await api.get<PersonnelTaskType[]>("/personnel-tasks/types/defaults")).data
  }
}
export const createTaskType = async (payload: TaskTypePayload) => (await api.post<PersonnelTaskType>("/personnel-tasks/types", payload)).data
export const updateTaskType = async (id: string, payload: TaskTypePayload) => (await api.patch<PersonnelTaskType>(`/personnel-tasks/types/${id}`, payload)).data
export const deleteTaskType = async (id: string) => (await api.delete<{ deleted?: boolean; deactivated?: boolean }>(`/personnel-tasks/types/${id}`)).data
/** Matris / seçim etiketi: "İç tetkik — İç Tetkikçi". */
export const taskTypeLabel = (t: Pick<PersonnelTaskType, "name" | "roleLabel">) => [t.name, t.roleLabel].filter(Boolean).join(" — ")
/** Sıralı türleri grup adına göre toplar (sıra korunur). */
export function groupTaskTypes<T extends Pick<PersonnelTaskType, "groupName">>(types: T[]) {
  const groups: { name: string; types: T[] }[] = []
  for (const t of types) {
    const g = groups.find((x) => x.name === t.groupName)
    if (g) g.types.push(t)
    else groups.push({ name: t.groupName, types: [t] })
  }
  return groups
}

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  PENDING: "Kabul bekliyor",
  ACCEPTED: "Kabul edildi",
  REJECTED: "Reddedildi",
  COMPLETED: "Tamamlandı",
  CANCELLED: "İptal edildi",
}

export const TASK_STATUS_CLASS: Record<TaskStatus, string> = {
  PENDING: "bg-amber-100 text-amber-700 border-amber-200",
  ACCEPTED: "bg-blue-100 text-blue-700 border-blue-200",
  REJECTED: "bg-red-100 text-red-700 border-red-200",
  COMPLETED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  CANCELLED: "bg-gray-100 text-gray-600 border-gray-200",
}

export interface EligibilityIssue { code: string; severity: "warn" | "block"; message: string }
export interface EligibilityResult { mode: "warn" | "block"; issues: EligibilityIssue[]; blocking: boolean }

export interface PersonnelTask {
  id: string
  personnelId: string
  elevatorProjectId: string | null
  type: TaskType
  /** Şirketin görev türü adı (sunucu ekler). */
  typeName?: string
  title: string
  description: string | null
  plannedDate: string | null
  dueDate: string | null
  location: string | null
  status: TaskStatus
  eligibility: { mode: string; issues: EligibilityIssue[]; overrideReason?: string } | null
  rejectionReason: string | null
  completionNote: string | null
  respondedAt: string | null
  completedAt: string | null
  createdAt: string
  personnel?: { id: string; code: string; firstName: string; lastName: string }
  elevatorProject?: { id: string; dosyaNo: string } | null
  /** Görevi doğuran kaynak: `form:<set öğesi>` (form doldurma) ya da alan anahtarı. */
  sourceFieldKey?: string | null
}

/** Görevin dosyadaki yeri: form görevi Denetim Seti bölümüne, öteki dosyanın başına. */
export function gorevDosyaYolu(t: Pick<PersonnelTask, "elevatorProject" | "sourceFieldKey">): string | null {
  if (!t.elevatorProject) return null
  return `/faaliyetler/${t.elevatorProject.id}${t.sourceFieldKey?.startsWith("form:") ? "?bolum=denetim" : ""}`
}

export interface CreateTaskPayload {
  personnelId: string
  type: TaskType
  title?: string
  description?: string
  elevatorProjectId?: string
  plannedDate?: string
  dueDate?: string
  location?: string
  confirm?: boolean
  overrideReason?: string
}

export const fetchTasks = async (params: { personnelId?: string; elevatorProjectId?: string; status?: TaskStatus }) =>
  (await api.get<PersonnelTask[]>("/personnel-tasks", { params })).data
export const fetchEligibility = async (personnelId: string, params: { elevatorProjectId?: string; type?: TaskType; date?: string }) =>
  (await api.get<EligibilityResult>(`/personnel-tasks/eligibility/${personnelId}`, { params })).data
export const createTask = async (payload: CreateTaskPayload) => (await api.post<PersonnelTask>("/personnel-tasks", payload)).data
export const cancelTask = async (id: string) => (await api.post(`/personnel-tasks/${id}/cancel`)).data
export const fetchMyTasks = async () => (await api.get<{ person: { id: string; firstName: string; lastName: string; code: string } | null; tasks: PersonnelTask[] }>("/personnel-tasks/mine")).data
export const respondTask = async (id: string, action: "accept" | "reject" | "complete", body?: { reason?: string; note?: string }) =>
  (await api.post<PersonnelTask>(`/personnel-tasks/${id}/${action}`, body ?? {})).data
export const fetchEligibilitySettings = async (companyId?: string) =>
  (await api.get<{ personnelEligibilityMode: "warn" | "block" }>("/personnel-tasks/settings", { params: { companyId } })).data
export const saveEligibilitySettings = async (mode: "warn" | "block", companyId?: string) =>
  (await api.put("/personnel-tasks/settings", { personnelEligibilityMode: mode, companyId })).data

/** Sunucunun uygunluk hatasından bulguları çıkarır (`errors.issues`). */
export function eligibilityIssuesOf(error: unknown): EligibilityIssue[] | null {
  const issues = (error as { response?: { data?: { errors?: { issues?: EligibilityIssue[] } } } })?.response?.data?.errors?.issues
  return Array.isArray(issues) ? issues : null
}
